import React, { useContext } from "react"
import { Redirect } from "react-router-dom"
import Header from "../../components/shared/Header/Header"
import Footer from "../../components/shared/Footer/Footer"
import { FeedSpinner } from "../../components/shared/Spinner"
import { RequestError } from "../../components/shared/RequestError"
import { UserContext } from "../../components/HomeApp"
import { Currency } from "../../util/numbers"
import { SmartDateFormat } from "../../util/dates"
import { usePostPayout } from "../../hooks/usePostPayout"
import PerformersInterestTable from "./PerformersInterestTable"
import style from "../../components/shared/PageStyle.module.css"

const PostPayoutResolver: React.FC<{ postId: string }> = ({ postId }) => {
  const { user } = useContext(UserContext)
  const { data, status, error } = usePostPayout(Number(postId))

  if (status === "loading") return <FeedSpinner />
  if (status === "error") return <RequestError error={error} />
  if (data?.payout_id) return <Redirect to={`/dashboard/payouts/${data.payout_id}`} />

  const post = data?.post

  return (
    <div className={style.rootElStyle}>
      <Header user={user} />
      <div className="grid grid-cols-12 w-full pb-2 px-4">
        <div className="col-span-12">
          <h1 className="border-b-4 mt-4 border-black font-bold mb-4 text-3xl uppercase">
            Payout Distribution
          </h1>

          {post && (
            <div className="bg-gray-100 border border-gray-300 rounded p-4">
              <h2 className="font-bold text-lg uppercase mb-2">Show Summary</h2>
              <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
                <div className="font-semibold">Event</div>
                <div>{post.title}</div>
                <div className="font-semibold">Date</div>
                <div>{SmartDateFormat({ dateStr: post.published_at })}</div>
                {post.has_bandnada_tickets && (
                  <>
                    <div className="font-semibold">Tickets Sold</div>
                    <div>{post.tickets_sold}</div>
                    <div className="font-semibold">Total Revenue</div>
                    <div>{Currency(post.total_revenue)}</div>
                    <div className="font-semibold">bandNada Fee</div>
                    <div>{Currency(post.bn_share)}</div>
                    <div className="font-semibold text-base border-t border-gray-400 pt-1 mt-1">
                      Estimated Payout
                    </div>
                    <div className="font-bold text-base border-t border-gray-400 pt-1 mt-1">
                      {Currency(post.band_share)}
                    </div>
                  </>
                )}
              </div>
              {!post.has_bandnada_tickets && (
                <div className="text-sm text-gray-600 mt-2">
                  This show didn't sell tickets through bandNada, so there's no payout to
                  calculate.
                </div>
              )}
            </div>
          )}

          <PerformersInterestTable performers={data?.performers ?? []} />

          {post?.has_bandnada_tickets && (
            <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-4 text-sm text-gray-700">
              Distribution isn't available yet. Once this show's payout has been calculated and
              released, you'll be able to split funds with performers and collaborators here.
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default PostPayoutResolver
